import * as Dialog from '@radix-ui/react-dialog';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Check, ChevronDown, X } from 'lucide-react';
import { useId, useState } from 'react';

import { DangerButton } from '@/components/danger/DangerButton';
import { cn } from '@/lib/cn';
import { CONFIGURED_ENVIRONMENTS, type MeshEnvironment } from '@/lib/env';
import {
  activeEnvironment,
  canSwitchEnvironments,
  environmentApiUrl,
  switchEnvironment,
} from '@/lib/environment';
import { environmentStyle } from '@/lib/environment-style';

import { EnvironmentBadge, EnvironmentBadgeFace } from './EnvironmentBadge';

/**
 * The environment control in the top bar.
 *
 * Falls back to the plain badge when this build has one reachable target, so a
 * single-environment deploy behaves exactly as it always has — there is no
 * affordance to click and nothing to get wrong.
 */
export function EnvironmentControl({ className }: { className?: string }) {
  if (!canSwitchEnvironments) return <EnvironmentBadge className={className} />;
  return <EnvironmentSwitcher className={className} />;
}

function EnvironmentSwitcher({ className }: { className?: string }) {
  const [pendingEnvironment, setPendingEnvironment] = useState<MeshEnvironment | null>(null);
  const [storageError, setStorageError] = useState<string | null>(null);
  const style = environmentStyle();

  function select(next: MeshEnvironment) {
    if (next === activeEnvironment) return;

    // Production is the one target where a mis-click has real consequences, so
    // it is the one target that cannot be reached from the menu alone.
    if (next === 'production') {
      setPendingEnvironment(next);
      return;
    }

    commit(next);
  }

  function commit(next: MeshEnvironment) {
    try {
      switchEnvironment(next);
    } catch (cause) {
      setPendingEnvironment(null);
      setStorageError(cause instanceof Error ? cause.message : 'Could not switch environment.');
    }
  }

  return (
    <>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            aria-label={`Environment: ${style.label}. Change environment.`}
            className={cn(
              'flex h-8 flex-none items-center gap-[7px] rounded-md px-2.5 outline-none',
              'hover:brightness-125 focus-visible:brightness-125',
              className,
            )}
            style={{ background: style.bg, border: `1px solid ${style.border}` }}
          >
            <EnvironmentBadgeFace
              trailing={
                <ChevronDown
                  size={12}
                  aria-hidden
                  className="-mr-0.5 opacity-70"
                  style={{ color: style.text }}
                />
              }
            />
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            sideOffset={6}
            className="border-bd bg-card z-130 min-w-[260px] rounded-lg border p-1 shadow-[var(--shadow-lg)]"
          >
            <div
              className="font-head text-tx3 px-2.5 pt-1.5 pb-1 text-[10.5px] font-bold uppercase"
              style={{ letterSpacing: '0.16em' }}
            >
              Environment
            </div>

            {CONFIGURED_ENVIRONMENTS.map((environment) => {
              const target = environmentStyle(environment);
              const isActive = environment === activeEnvironment;

              return (
                <DropdownMenu.Item
                  key={environment}
                  onSelect={() => select(environment)}
                  className="hover:bg-elev focus:bg-elev flex cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2 outline-none select-none"
                >
                  <span
                    aria-hidden
                    className="size-1.5 flex-none rotate-45"
                    style={{ background: target.dot }}
                  />
                  <span className="min-w-0 flex-1">
                    <span
                      className="font-head block text-[12.5px] font-bold uppercase"
                      style={{ letterSpacing: '0.14em', color: target.text }}
                    >
                      {target.label}
                    </span>
                    {/* The URL is shown because "Production" over localhost is
                        exactly the confusion the badge is meant to prevent. */}
                    <span className="mesh-mono text-tx3 block truncate text-[10.5px]">
                      {environmentApiUrl(environment)}
                    </span>
                  </span>
                  {isActive && <Check size={13} className="text-tx2 flex-none" aria-hidden />}
                </DropdownMenu.Item>
              );
            })}

            <p className="text-tx3 border-bd mt-1 border-t px-2.5 py-2 text-[11px] leading-[1.45]">
              Switching reloads the console and signs you in again against that environment.
            </p>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      <ProductionSwitchDialog
        open={pendingEnvironment !== null}
        onOpenChange={(open) => !open && setPendingEnvironment(null)}
        onConfirm={() => pendingEnvironment && commit(pendingEnvironment)}
      />

      {storageError && (
        <p role="alert" className="sr-only">
          {storageError}
        </p>
      )}
    </>
  );
}

const TYPE_TO_CONFIRM = 'production';

/**
 * Type-to-confirm before pointing the console at production.
 *
 * Purpose-built rather than reusing `ConfirmDialog` at Tier 3: that dialog
 * requires a reason and states it is "permanently attached to the audit record",
 * which would be a lie here — switching environments is a local act that writes
 * nothing to any API.
 */
function ProductionSwitchDialog({
  open,
  onOpenChange,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}) {
  const [typed, setTyped] = useState('');
  const typeId = useId();
  const style = environmentStyle('production');
  const target = environmentApiUrl('production');

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        setTyped('');
        onOpenChange(next);
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay
          className="motion-safe:animate-mesh-fade fixed inset-0 z-140"
          style={{ background: 'var(--scrim-danger)' }}
        />
        <Dialog.Content
          aria-describedby={undefined}
          className="border-bd bg-card motion-safe:animate-mesh-sheet-up fixed inset-x-0 bottom-0 z-150 flex max-h-[90vh] flex-col overflow-hidden rounded-t-xl border-t shadow-[var(--shadow-lg)] sm:inset-x-0 sm:top-[14vh] sm:bottom-auto sm:mx-auto sm:w-[92vw] sm:max-w-[460px] sm:rounded-xl sm:border"
          style={{ borderTopColor: style.hairline, borderTopWidth: 2 }}
        >
          <div
            className="border-bd flex flex-none items-start gap-2.5 border-b px-4 py-3.5"
            style={{ background: style.bg }}
          >
            <div className="min-w-0 flex-1">
              <div
                className="font-head text-[10.5px] font-bold uppercase"
                style={{ letterSpacing: '0.16em', color: style.text }}
              >
                Switch environment
              </div>
              <Dialog.Title className="font-head text-tx mt-0.5 text-[19px] leading-tight font-semibold">
                Point the console at production
              </Dialog.Title>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Cancel"
                className="text-tx3 hover:bg-elev hover:text-tx grid size-8 flex-none place-items-center rounded-md"
              >
                <X size={15} />
              </button>
            </Dialog.Close>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3.5">
            <p className="text-tx2 text-[13px] leading-[1.55]">
              Every action you take after this reload applies to live data — real leagues, real
              seasons, real balances. Mocks are disabled in production regardless of your local
              setting.
            </p>
            <p className="mesh-mono text-tx3 mt-2.5 text-[11.5px] break-all">{target}</p>

            <div className="mt-3.5">
              <label
                htmlFor={typeId}
                className="font-head text-tx2 block text-[11.5px] font-semibold uppercase"
                style={{ letterSpacing: '0.14em' }}
              >
                Type <span className="mesh-mono lowercase">{TYPE_TO_CONFIRM}</span> to confirm
              </label>
              <input
                id={typeId}
                value={typed}
                onChange={(event) => setTyped(event.target.value)}
                autoComplete="off"
                spellCheck={false}
                className="mesh-mono border-bd bg-bg-alt text-tx focus-visible:border-bd-s mt-1.5 w-full rounded-lg border px-2.5 py-2 text-[13px] outline-none"
              />
            </div>
          </div>

          <div className="border-bd flex flex-none flex-col-reverse gap-2 border-t px-4 py-3 sm:flex-row sm:justify-end">
            <Dialog.Close asChild>
              <button
                type="button"
                className="border-bd bg-elev text-tx2 hover:text-tx min-h-11 rounded-lg border px-3.5 text-[13px] font-medium"
              >
                Cancel
              </button>
            </Dialog.Close>
            <DangerButton
              tier={3}
              variant="filled"
              disabled={typed.trim() !== TYPE_TO_CONFIRM}
              onClick={onConfirm}
            >
              Switch to production
            </DangerButton>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
